// Balansa apļa SVG: vērtību vilkšana, saišu zīmēšana (vilkšana vai divi klikšķi), fokuss un uzvednes.

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { clamp, fmt, heat, mean, uniq } from '@balansa/shared';
import { B0, B1, H0, H1, R, RA, annular, arcPath, f2, fitLabel, layout, normAng, polar, pt, rad, wedge } from '../lib/geometry';
import { linkProblem } from '../lib/helpers';
import { useApp, useWheel } from '../store';
import { useTip } from './Tooltip';

interface Drag {
  id: string;
  kind: 'value' | 'link';
  x: number;
  y: number;
  moved: boolean;
  pid: number;
  target: string | null;
}

const DRAFT_STYLE = { display: 'none' };

export function Wheel() {
  const { wheel, idx } = useWheel();
  const ui = useApp((s) => s.ui);
  const st = wheel.settings;
  const th = st.threshold;
  const L = useMemo(() => layout(idx, st.view), [idx, st.view]);
  const svgRef = useRef<SVGSVGElement>(null);
  const draftRef = useRef<SVGPathElement>(null);
  const drag = useRef<Drag | null>(null);
  const suppressClick = useRef(false);
  const dropRef = useRef<string | null>(null);
  const [dropId, setDropIdState] = useState<string | null>(null);
  const setDropId = (v: string | null) => {
    dropRef.current = v;
    setDropIdState(v);
  };
  const tip = useTip;
  const A = useApp.getState;

  // ---------- saites (ceļi, nobīdes paralēlām saitēm) ----------
  const linkItems = useMemo(() => {
    if (st.links === 'none') return [];
    const groups = new Map<string, Item[]>();
    type Item = { l: (typeof wheel.links)[number]; fa: number; ta: number; fwd: boolean; off: number };
    const items: Item[] = [];
    wheel.links.forEach((l) => {
      if (!idx.node.has(l.fromId) || !idx.node.has(l.toId)) return;
      const fa = L.anchor(l.fromId);
      const ta = L.anchor(l.toId);
      if (fa == null || ta == null) return;
      const kf = Math.round(fa * 2);
      const kt = Math.round(ta * 2);
      if (kf === kt) return;
      const key = kf < kt ? kf + '|' + kt : kt + '|' + kf;
      if (!groups.has(key)) groups.set(key, []);
      const it = { l, fa, ta, fwd: kf < kt, off: 0 };
      groups.get(key)!.push(it);
      items.push(it);
    });
    groups.forEach((g) => g.forEach((it, i) => (it.off = (i - (g.length - 1) / 2) * 34)));
    const unit = (a: [number, number], b: [number, number]): [number, number] => {
      const x = b[0] - a[0];
      const y = b[1] - a[1];
      const d = Math.hypot(x, y) || 1;
      return [x / d, y / d];
    };
    return items.map((it) => {
      const l = it.l;
      const P1 = polar(it.fa, RA);
      const P2 = polar(it.ta, RA);
      let C: [number, number] = [((P1[0] + P2[0]) / 2) * 0.3, ((P1[1] + P2[1]) / 2) * 0.3];
      const Aa = it.fwd ? P1 : P2;
      const Bb = it.fwd ? P2 : P1;
      const dx = Bb[0] - Aa[0];
      const dy = Bb[1] - Aa[1];
      const len = Math.hypot(dx, dy) || 1;
      C = [C[0] - (dy / len) * it.off, C[1] + (dx / len) * it.off];
      const u1 = unit(P1, C);
      const u2 = unit(P2, C);
      const S: [number, number] = [P1[0] + u1[0] * 2, P1[1] + u1[1] * 2];
      const E: [number, number] = [P2[0] + u2[0] * 3, P2[1] + u2[1] * 3];
      const M: [number, number] = [0.25 * S[0] + 0.5 * C[0] + 0.25 * E[0], 0.25 * S[1] + 0.5 * C[1] + 0.25 * E[1]];
      const col = l.type === 'take' ? 'var(--danger)' : idx.color(l.fromId);
      const chain = uniq(idx.anc.get(l.fromId)!.concat(idx.anc.get(l.toId)!));
      const ends = uniq(L.main ? [idx.top.get(l.fromId)!, idx.top.get(l.toId)!] : idx.leaves.get(l.fromId)!.concat(idx.leaves.get(l.toId)!));
      const txt = (l.type === 'give' ? 'dod' : 'atņem') + (l.label ? ' ' + l.label : '');
      return { l, d: 'M' + pt(S) + 'Q' + pt(C) + ' ' + pt(E), M, col, sw: (1.6 + l.strength * 1.1).toFixed(1), chain, ends, txt, w: Math.round(txt.length * 6.6 + 18) };
    });
  }, [wheel.links, idx, L, st.links]);

  const markers = useMemo(() => {
    const m = new Map<string, string>();
    linkItems.forEach((it) => {
      if (!m.has(it.col)) m.set(it.col, 'mk' + m.size);
    });
    return m;
  }, [linkItems]);

  // ---------- fokuss (uzbraukšana / izvēle) ----------
  const LK = ui.hoverLink;
  const F = LK ? null : ui.hoverId || ui.selected;
  const hl = new Set(linkItems.filter((it) => (LK ? it.l.id === LK : F ? it.chain.includes(F) : false)).map((it) => it.l.id));
  const rel = new Set<string>();
  if (F && idx.node.has(F)) (L.main ? [idx.top.get(F)!] : idx.leaves.get(F)!).forEach((x) => rel.add(x));
  linkItems.forEach((it) => {
    if (hl.has(it.l.id)) it.ends.forEach((x) => rel.add(x));
  });
  const focusing = !!(F || LK);

  const flashSet = new Set<string>();
  ui.flash?.forEach((t) => {
    if (idx.node.has(t)) (L.main ? [idx.top.get(t)!] : idx.leaves.get(t)!).forEach((x) => flashSet.add(x));
  });

  // ---------- notikumi ----------
  const toSvg = (e: { clientX: number; clientY: number }) => {
    const svg = svgRef.current!;
    const p = svg.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;
    return p.matrixTransform(svg.getScreenCTM()!.inverse());
  };
  const dropTargetAt = (e: { clientX: number; clientY: number }) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const svg = svgRef.current;
    if (!el || !svg || !svg.contains(el)) return null;
    const d = el.closest<SVGElement>('.dot');
    if (d) return d.dataset.id || null;
    const p = el.closest<SVGElement>('[data-pick]');
    return p ? p.dataset.pick || null : null;
  };
  const drawDraft = (src: string, e: { clientX: number; clientY: number }) => {
    const { idx: I } = A();
    let tgt = dropTargetAt(e);
    if (tgt && I && linkProblem(I, src, tgt)) tgt = null;
    if (tgt !== dropRef.current) setDropId(tgt);
    const path = draftRef.current;
    const sa = L.anchor(src);
    if (!path || sa == null) return tgt;
    path.style.display = '';
    const S = polar(sa, RA);
    const ta = tgt ? L.anchor(tgt) : undefined;
    if (tgt && ta != null) {
      const E = polar(ta, RA);
      const C: [number, number] = [((S[0] + E[0]) / 2) * 0.3, ((S[1] + E[1]) / 2) * 0.3];
      path.setAttribute('d', 'M' + pt(S) + 'Q' + pt(C) + ' ' + pt(E));
      path.classList.add('snap');
    } else {
      const p = toSvg(e);
      path.setAttribute('d', 'M' + pt(S) + 'L' + f2(p.x) + ' ' + f2(p.y));
      path.classList.remove('snap');
    }
    return tgt;
  };
  const clearDraft = () => {
    const p = draftRef.current;
    if (p) {
      p.style.display = 'none';
      p.removeAttribute('d');
    }
    if (dropRef.current) setDropId(null);
  };

  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    if (e.button > 0) return;
    const t = e.target as Element;
    const dot = t.closest<SVGElement>('.dot');
    const src = dot || t.closest<SVGElement>('[data-pick]');
    if (!src) return;
    const id = dot ? dot.dataset.id! : src.dataset.pick!;
    if (!idx.node.has(id)) return;
    // Vērtību maina, velkot punktu; saiti zīmē, velkot nosaukumu/segmentu vai jebko režīmā "Zīmēt saites".
    const kind = dot && ui.mode !== 'link' ? (dot.classList.contains('drag') ? 'value' : 'link') : 'link';
    drag.current = { id, kind, x: e.clientX, y: e.clientY, moved: false, pid: e.pointerId, target: null };
    try {
      svgRef.current!.setPointerCapture(e.pointerId);
    } catch {
      /* ignorē */
    }
    e.preventDefault();
  };

  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    const s = A();
    if (d) {
      if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 4) return;
      if (!d.moved) {
        d.moved = true;
        s.setUi({ dragging: true });
      }
      if (d.kind === 'value') {
        document.body.classList.add('dragging');
        const p = toSvg(e);
        const a = rad(L.axisAngle.get(d.id) ?? 0);
        const v = clamp(Math.round(((p.x * Math.cos(a) + p.y * Math.sin(a)) / R) * 10), 0, 10);
        s.setNodeValue(d.id, v);
        tip.getState().show('node:' + d.id, e);
        return;
      }
      document.body.classList.add('linking');
      if (s.ui.hoverId || s.ui.hoverLink) s.setHover(null, null);
      d.target = drawDraft(d.id, e);
      const I = s.idx!;
      tip.getState().showCustom(
        <>
          <div className="tt-h">
            <i style={{ background: I.color(d.id) }} />
            {I.node.get(d.id)!.name} → {d.target ? I.node.get(d.target)!.name : '…'}
          </div>
          <div className="tt-hint">{d.target ? 'Atlaid — izveidosi saiti' : 'Velc līdz citai jomai'}</div>
        </>,
        e
      );
      return;
    }
    const t = (e.target as Element).closest<SVGElement>('[data-tip]');
    if (s.ui.mode === 'link' && s.ui.linkSrc) drawDraft(s.ui.linkSrc, e);
    if (!t) {
      tip.getState().hide();
      s.setHover(null, null);
      return;
    }
    const key = t.dataset.tip!;
    tip.getState().show(key, e);
    const i = key.indexOf(':');
    if (key.slice(0, i) === 'node') s.setHover(key.slice(i + 1), null);
    else s.setHover(null, key.slice(i + 1));
  };

  const endDrag = (cancel: boolean) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    const s = A();
    try {
      svgRef.current?.releasePointerCapture(d.pid);
    } catch {
      /* ignorē */
    }
    document.body.classList.remove('dragging', 'linking');
    if (d.moved) s.setUi({ dragging: false });
    clearDraft();
    suppressClick.current = true;
    setTimeout(() => (suppressClick.current = false), 0);
    if (cancel) {
      tip.getState().hide();
      return;
    }
    if (!d.moved) {
      s.pick(d.id);
      return;
    }
    if (d.kind === 'value') return;
    tip.getState().hide();
    if (d.target) s.openDialog({ kind: 'link', from: d.id, to: d.target });
  };

  const onClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (suppressClick.current) return;
    const s = A();
    const t = e.target as Element;
    const lk = t.closest<SVGElement>('.link');
    if (lk) {
      tip.getState().hide();
      s.openDialog({ kind: 'link', id: lk.dataset.link });
      return;
    }
    const p = t.closest<SVGElement>('[data-pick]');
    if (p) {
      s.pick(p.dataset.pick!);
      return;
    }
    if (t.closest('.dot')) return;
    if (s.ui.mode === 'link') {
      if (s.ui.linkSrc) {
        s.setUi({ linkSrc: null });
        clearDraft();
      }
      return;
    }
    if (s.ui.selected) s.setUi({ selected: null });
  };

  const onKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
    const g = (e.target as Element).closest<SVGElement>('.dot');
    if (!g) return;
    const id = g.dataset.id!;
    const s = A();
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      s.pick(id);
      return;
    }
    if (!g.classList.contains('drag')) return;
    const d = ({ ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1 } as Record<string, number>)[e.key];
    if (!d) return;
    e.preventDefault();
    const n = s.idx!.node.get(id)!;
    s.setNodeValue(id, clamp(Math.round(n.value) + d, 0, 10));
  };

  // Uzvedne nedrīkst “pielipt”, ja pele pamet apli ārpus SVG notikumiem.
  useEffect(() => {
    const onMove = (e: globalThis.PointerEvent) => {
      if (drag.current || !svgRef.current || svgRef.current.contains(e.target as Node)) return;
      if (useTip.getState().key) useTip.getState().hide();
      const s = A();
      if ((s.ui.hoverId || s.ui.hoverLink) && !(e.target as Element).closest?.('#tree, #link-list, #analysis, #compare-table, #e-rows'))
        s.setHover(null, null);
    };
    document.addEventListener('pointermove', onMove);
    return () => document.removeEventListener('pointermove', onMove);
  }, [A]);

  // Režīma maiņa: notīra melnraksta līniju.
  useEffect(() => {
    if (!ui.linkSrc && !drag.current) clearDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.linkSrc, ui.mode]);

  const cls = 'v-' + st.view + ' lm-' + st.links + (ui.mode === 'link' ? ' linkmode' : '') + (focusing ? ' focusing' : '');
  const viewBox = L.main ? '-338 -338 676 676' : '-448 -448 896 896';

  if (!idx.tops.length)
    return (
      <svg id="wheel" ref={svgRef} viewBox={viewBox} className={cls} role="img" aria-label="Balansa apļa diagramma">
        <text className="empty-t" x="0" y="0" textAnchor="middle">
          Pievieno pirmo jomu panelī “Jomas”
        </text>
      </svg>
    );

  const vOf = (id: string) => idx.val.get(id)!;
  const avg = mean(L.axes.map((a) => vOf(a.id)));
  const snap = st.compare ? wheel.snapshots.find((s) => s.id === st.compare) : null;
  const ta = -90 + L.step / 2;
  const rDot = L.main ? 15 : 10;
  const src = ui.linkSrc;

  return (
    <svg
      id="wheel"
      ref={svgRef}
      viewBox={viewBox}
      className={cls}
      role="img"
      aria-label="Balansa apļa diagramma"
      data-testid="wheel"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => endDrag(false)}
      onPointerCancel={() => endDrag(true)}
      onPointerLeave={() => {
        if (!drag.current) {
          tip.getState().hide();
          A().setHover(null, null);
          clearDraft();
        }
      }}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <defs>
        <marker id="mk-draft" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="3.4" markerHeight="3.4" orient="auto">
          <path d="M0 0L10 5L0 10z" style={{ fill: 'var(--ink)' }} />
        </marker>
        {[...markers].map(([col, id]) => (
          <marker key={id} id={id} viewBox="0 0 10 10" refX="7" refY="5" markerWidth="3.2" markerHeight="3.2" orient="auto">
            <path d="M0 0L10 5L0 10z" style={{ fill: col }} />
          </marker>
        ))}
      </defs>

      {L.sectors.map((s) => (
        <path key={'s' + s.id} className="sector" d={wedge(R, s.a0, s.a1)} style={{ fill: idx.color(s.id) }} />
      ))}
      {th > 0 ? <circle className="core" r={f2((R * th) / 10)} /> : null}
      {[2, 4, 6, 8, 10].map((k) => (
        <circle key={'r' + k} className={'ring' + (k === 10 ? ' outer' : '')} r={(R * k) / 10} />
      ))}
      {L.axes.map((a) => {
        const p = polar(a.angle, R);
        return <line key={'a' + a.id} className="axis" x1="0" y1="0" x2={f2(p[0])} y2={f2(p[1])} />;
      })}
      {[2, 4, 6, 8, 10].map((k) => {
        const p = polar(ta, (R * k) / 10);
        return (
          <text key={'t' + k} className="tick" x={f2(p[0])} y={f2(p[1])}>
            {k}
          </text>
        );
      })}

      <circle className="avgc" r={f2((R * avg) / 10)} />
      {snap ? (
        <polygon
          className="ghost"
          points={L.axes.map((a) => pt(polar(a.angle, (R * (snap.vals[a.id] != null ? snap.vals[a.id] : vOf(a.id))) / 10))).join(' ')}
        />
      ) : null}
      <polygon className="poly" points={L.axes.map((a) => pt(polar(a.angle, (R * vOf(a.id)) / 10))).join(' ')} />

      {L.sectors.map((s) => {
        const gap = Math.min(0.8, (s.a1 - s.a0) * 0.04);
        const a0 = s.a0 + gap;
        const a1 = s.a1 - gap;
        const rm = (B0 + B1) / 2;
        const pid = 'bp-' + s.id;
        const fit = fitLabel(idx.node.get(s.id)!.name, rm * rad(a1 - a0) - 12, 13);
        return (
          <g
            key={'b' + s.id}
            className={'band' + (ui.selected === s.id ? ' sel' : '') + (src === s.id ? ' is-src' : '') + (dropId === s.id ? ' drop' : '')}
            data-tip={'node:' + s.id}
            data-pick={s.id}
          >
            <path className="bandp" d={annular(B0, B1, a0, a1)} style={{ fill: idx.color(s.id) }} />
            <path id={pid} d={arcPath(rm, a0, a1)} fill="none" />
            <text dy="0.35em" textAnchor="middle" style={{ fontSize: fit.size + 'px' }}>
              <textPath href={'#' + pid} startOffset="50%">
                {fit.text}
              </textPath>
            </text>
          </g>
        );
      })}

      {L.main
        ? L.sectors.map((s) => {
            const lv = idx.leaves.get(s.id)!;
            const span = s.a1 - s.a0;
            const g = Math.min(0.8, (span / lv.length) * 0.08);
            return lv.map((lid, j) => {
              const a0 = s.a0 + (span * j) / lv.length + g;
              const a1 = s.a0 + (span * (j + 1)) / lv.length - g;
              return (
                <path
                  key={'h' + s.id + lid}
                  className={'heat' + (ui.selected === lid ? ' sel' : '') + (src === lid ? ' is-src' : '') + (dropId === lid ? ' drop' : '')}
                  d={annular(H0, H1, a0, a1)}
                  style={{ fill: heat(vOf(lid)) }}
                  data-tip={'node:' + lid}
                  data-pick={lid}
                />
              );
            });
          })
        : null}

      <g className="links-layer">
        {linkItems.map((it) => (
          <g key={it.l.id} className={'link ' + it.l.type + (hl.has(it.l.id) ? ' hl' : '')} data-link={it.l.id} data-tip={'link:' + it.l.id}>
            <path className="hit" d={it.d} />
            <path
              className="l"
              d={it.d}
              style={{ stroke: it.col, strokeWidth: it.sw }}
              strokeDasharray={it.l.type === 'take' ? '7 6' : undefined}
              markerEnd={`url(#${markers.get(it.col)})`}
            />
            <g className="lbl" transform={'translate(' + pt(it.M) + ')'}>
              <rect x={-it.w / 2} y="-11" width={it.w} height="22" rx="11" style={{ stroke: it.col }} />
              <text>{it.txt}</text>
            </g>
          </g>
        ))}
      </g>

      {!L.main
        ? L.axes.map((a) => {
            const n = idx.node.get(a.id)!;
            const v = vOf(a.id);
            const cn = normAng(a.angle);
            const right = cn > -90.5 && cn < 89.5;
            const p = polar(a.angle, B1 + 9);
            const name = n.name.length > 20 ? n.name.slice(0, 19) + '…' : n.name;
            return (
              <text
                key={'ll' + a.id}
                className={'leaf-lbl' + (v <= th ? ' low' : '') + (src === a.id ? ' is-src' : '') + (dropId === a.id ? ' drop' : '')}
                transform={'translate(' + pt(p) + ') rotate(' + f2(right ? cn : cn + 180) + ')'}
                textAnchor={right ? 'start' : 'end'}
                data-tip={'node:' + a.id}
                data-pick={a.id}
              >
                {name}
              </text>
            );
          })
        : null}

      {L.axes.map((a) => {
        const id = a.id;
        const n = idx.node.get(id)!;
        const v = vOf(id);
        const p = polar(a.angle, (R * v) / 10);
        const editable = !idx.kids.get(id)!.length;
        const low = v <= th;
        const sel = ui.selected === id;
        return (
          <g
            key={'d' + id}
            className={
              'dot' + (editable ? ' drag' : '') + (low ? ' low' : '') + (sel ? ' sel' : '') + (src === id ? ' is-src' : '') +
              (flashSet.has(id) ? ' flash' : '') + (rel.has(id) ? ' rel' : '') + (dropId === id ? ' drop' : '')
            }
            data-id={id}
            data-tip={'node:' + id}
            tabIndex={0}
            role="slider"
            aria-label={n.name}
            aria-valuemin={0}
            aria-valuemax={10}
            aria-valuenow={Math.round(v * 100) / 100}
            aria-readonly={editable ? undefined : true}
            transform={'translate(' + pt(p) + ')'}
          >
            {low ? <circle className="halo" r={rDot + 5} /> : null}
            {sel ? <circle className="selring" r={rDot + 9} /> : null}
            <circle className="d" r={rDot} style={{ fill: idx.color(id) }} />
            <text>{fmt(v)}</text>
          </g>
        );
      })}

      <path ref={draftRef} className="draft" markerEnd="url(#mk-draft)" style={DRAFT_STYLE} />
    </svg>
  );
}
